import axios from 'axios';
import { contractorAuth } from './contractorAuth';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * Talking to the session endpoints, and nothing about WebRTC.
 *
 * Split out of the page for one reason: everything in here can be exercised
 * without a browser that has an RTCPeerConnection, and the parts that decide
 * WHO OFFERS and WHEN TO GIVE UP are pure functions of their arguments. Those
 * are precisely the decisions whose failures are silent - a connection that
 * never completes looks like a network problem and gets blamed on one - so
 * they belong somewhere a test can reach them directly.
 *
 * The transport is long polling rather than a WebSocket or SSE, and the
 * reasoning is in routes/sessions.js. From this side the consequence is
 * pleasant: every call is an ordinary axios request carrying the same
 * credential as the rest of the application, so there is no second
 * authentication path and no stream parser.
 */

/**
 * The credential, whoever is holding it.
 *
 * contractorAuth() returns undefined for a member of staff, and axios then
 * falls through to the interceptor in lib/apiClient, which attaches the Entra
 * token. So this one expression serves both kinds of caller - which is the
 * thing four pages got wrong by relying on the interceptor alone, and
 * answered 401 for every contractor.
 */
/*
 * AND A THIRD KIND OF CALLER, who has no Jarvis session at all: an outside
 * guest in a meeting (Gordon, 22 October - "guests join with a name only").
 * POST /join/:token/session hands the page a bearer of the form
 * `guest:<token>`, and from then on EVERY session request carries it.
 * Held here, module-level, so that the one place every helper reads its
 * credential from is the one place that knows about guests: nothing else
 * in the page has to be told, and recordings.js's authFor() - the banner's
 * store reads nothing, but the import is the same - is covered too.
 *
 * It is set before the guest's session page mounts and cleared when it
 * unmounts (GuestJoinPage), and a helper may still be handed an explicit
 * `auth` as its last argument, which wins over both.
 */
let guestAuth = null;

export function setGuestAuth(bearer) {
  guestAuth = bearer ? { headers: { Authorization: `Bearer ${bearer}` } } : null;
}

export const authFor = (override = null) => override || guestAuth || contractorAuth();

/* ------------------------------------------------------------------ opening */

/** The session behind an emailed invitation link. Creates it on first arrival. */
export const openForAppointment = (appointmentId) => axios
  .post(`${API_BASE}/appointments/${appointmentId}/session`, {}, authFor())
  .then((r) => r.data);

/** A session now, on a ticket, without an appointment. */
export const openForTicket = (ticketId) => axios
  .post(`${API_BASE}/tickets/${ticketId}/sessions`, {}, authFor())
  .then((r) => r.data);

/**
 * A session now, on the General thread with one person, with no ticket.
 *
 * Only works when the firm has allowed it (Settings, "Calls may be made
 * without a ticket"); otherwise the server answers 403 with a sentence that
 * says so, which the page shows as it is.
 */
// `thread` is a named topic with that person (migration 061): the call is
// then recorded in that conversation rather than in General, which is what
// nothing names.
export const openForThread = (kind, id, thread = null) => axios
  .post(`${API_BASE}/chat/with/${kind}/${id}/session`, {},
    { ...authFor(), params: thread ? { thread } : {} })
  .then((r) => r.data);

/**
 * A call to a chat group: everybody in it is rung at once.
 *
 * Like the General thread, only when the firm allows it; the server answers
 * 403 with its own sentence otherwise, and the page shows it as it is.
 */
export const openForGroup = (groupId) => axios
  .post(`${API_BASE}/chat/groups/${groupId}/session`, {}, authFor())
  .then((r) => r.data);

export const join = (sessionId, auth = null) => axios
  .post(`${API_BASE}/sessions/${sessionId}/join`, {}, authFor(auth))
  .then((r) => r.data);

export const iceConfig = (auth = null) => axios
  .get(`${API_BASE}/sessions/ice`, authFor(auth))
  .then((r) => r.data);

/* ------------------------------------------------------------------ writing */

export const sendSignal = (sessionId, body, auth = null) => axios
  .post(`${API_BASE}/sessions/${sessionId}/signal`, body, authFor(auth))
  .then((r) => r.data);

export const sendChat = (sessionId, body, auth = null) => axios
  .post(`${API_BASE}/sessions/${sessionId}/chat`, { body }, authFor(auth))
  .then((r) => r.data);

export const reportSharing = (sessionId, sharing, auth = null) => axios
  .post(`${API_BASE}/sessions/${sessionId}/sharing`, { sharing }, authFor(auth))
  .then((r) => r.data);

export const endSession = (sessionId, reason, auth = null) => axios
  .post(`${API_BASE}/sessions/${sessionId}/end`, { reason }, authFor(auth))
  .then((r) => r.data);

/**
 * Leaving a call without ending it for the others.
 *
 * With three or more people, hanging up is no longer the same act as ending
 * the call: the other two are still talking. The server answers with `ended`
 * true when this was the last but one, since a call with one person in it is
 * not a call.
 */
export const leaveSession = (sessionId, auth = null) => axios
  .post(`${API_BASE}/sessions/${sessionId}/leave`, {}, authFor(auth))
  .then((r) => r.data);

/**
 * The same, from a page that is closing.
 *
 * axios is gone with the page; a fetch marked keepalive is the browser's
 * promise to finish the request after the tab has closed. It needs the
 * credential NOW, which a contractor and a guest have (a token held here);
 * a member of staff's comes from MSAL asynchronously, so for them this
 * returns false and the presence heartbeat going stale does the job, as it
 * always has.
 */
export function leaveOnUnload(sessionId) {
  const auth = authFor()?.headers?.Authorization;
  if (!sessionId || !auth || typeof fetch !== 'function') return false;
  try {
    fetch(`${API_BASE}/sessions/${sessionId}/leave`, {
      method: 'POST', keepalive: true, body: '{}',
      headers: { Authorization: auth, 'Content-Type': 'application/json' }
    }).catch(() => {});
    return true;
  } catch {
    return false;
  }
}

/**
 * Ring somebody into a call that is already going. `kind` and `id` are the
 * chat's own - a row from /chat/people - so there is no second way of naming
 * a person to keep in step.
 */
export const inviteToSession = (sessionId, kind, id) => axios
  .post(`${API_BASE}/sessions/${sessionId}/invite`, { kind, id }, authFor())
  .then((r) => r.data);

/** The people this person may chat with, which is who they may add to a call. */
export const chatPeople = () => axios
  .get(`${API_BASE}/chat/people`, authFor())
  .then((r) => (Array.isArray(r.data) ? r.data : []));

/* ------------------------------------------------------------------ reading */

/**
 * One poll.
 *
 * `wait=0` asks the server to answer at once rather than holding the request,
 * which is what a page does when it is tearing down and wants the last of the
 * backlog without waiting twenty seconds for it.
 */
export const poll = (sessionId, { since = '0', chatSince = '0', wait = true,
  signal, auth = null } = {}) => axios.get(
  `${API_BASE}/sessions/${sessionId}/events`,
  {
    ...authFor(auth),
    params: { since, chat_since: chatSince, ...(wait ? {} : { wait: 0 }) },
    // A long poll holds for twenty seconds by design, so axios must not give
    // up before the server answers. Without this the default timeout - or a
    // page-wide one set later - would abort every poll and the session would
    // look like it had lost its connection every twenty seconds.
    timeout: 40000,
    signal
  }).then((r) => r.data);

/**
 * Poll until told to stop, handing each answer to `onBatch`.
 *
 * WHY THE BACKOFF ONLY APPLIES TO FAILURES. A poll that returns normally is
 * immediately reissued, because that is how an idle session stays responsive
 * and how candidate exchange keeps its latency. A poll that FAILS waits, and
 * waits longer each time, because the common reasons are a server restarting
 * mid-deploy and a laptop that has gone to sleep - and hammering either is
 * both useless and rude. It resets the moment one succeeds.
 *
 * Returns a function that stops it. Stopping is not optional: a loop left
 * running after the page has gone keeps a request in flight against a session
 * nobody is in, and keeps that participant looking present to the other side.
 */
export function pollLoop(sessionId, cursors, onBatch, onTrouble, auth = null) {
  let stopped = false;
  let since = cursors.since || '0';
  let chatSince = cursors.chatSince || '0';
  let failures = 0;
  const controller = new AbortController();

  const wait = (ms) => new Promise((r) => setTimeout(r, ms));

  (async () => {
    while (!stopped) {
      try {
        const batch = await poll(sessionId, { since, chatSince, signal: controller.signal, auth });
        if (stopped) return;
        failures = 0;
        if (onTrouble) onTrouble(null);
        since = batch.cursor || since;
        chatSince = batch.chat_cursor || chatSince;
        onBatch(batch);
        // The server reports the end rather than closing anything, so this is
        // where the loop finds out. Carrying on would poll an ended session
        // for ever. A guest in a meeting's waiting room is the exception: until
        // they are admitted the feed says { lobby: 'waiting' } and nothing
        // else - no state, because they are not in the call yet - and that is
        // a reason to keep asking, not to stop.
        if (batch.state !== 'open' && !batch.lobby) return;
      } catch (error) {
        if (stopped || error.code === 'ERR_CANCELED') return;
        failures += 1;
        // A 409 means this browser is not in the session - it ended, or the
        // join was lost. Retrying cannot fix either.
        if (error.response?.status === 409 || error.response?.status === 404) {
          if (onTrouble) onTrouble(error.response?.data?.error || 'This session has ended');
          return;
        }
        // A guest the organiser did not let in: the server says so with a
        // 403 naming the lobby, which is an answer rather than a fault, and
        // handed on as a batch so the page learns it where it learns
        // everything else. Retrying it would be knocking again.
        if (error.response?.status === 403 && error.response?.data?.lobby === 'refused') {
          onBatch({ lobby: 'refused', signals: [], chat: [], participants: [],
            state: error.response.data.state || 'open' });
          return;
        }
        if (onTrouble) {
          onTrouble(failures > 1
            ? 'Lost touch with the server — still trying.' : null);
        }
        await wait(Math.min(1000 * 2 ** (failures - 1), 15000));
      }
    }
  })();

  return () => { stopped = true; controller.abort(); };
}

/* Re-exported so a caller has one import. The rule itself lives in
 * lib/sessionRoles.js, which imports nothing and is therefore testable in
 * Node without a browser - see the note at the top of that file.
 */
export { shouldOffer, peersOf, namesOf } from './sessionRoles';
